/* eslint-disable max-lines */
import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Divider,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  OutlinedInput,
  Radio,
  RadioGroup,
  FormLabel,
  Card,
  CardContent,
  FormHelperText,
  Stack,
  LinearProgress,
  Fade
} from '@mui/material';
import {
  Business,
  Person,
  Assignment,
  Psychology,
  Security,
  CheckCircle,
  ArrowBack,
  ArrowForward,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

// Form field options from the JSON schema
const INDUSTRY_OPTIONS = [
  { value: 'TECHNOLOGY', label: 'Technology' },
  { value: 'HEALTHCARE', label: 'Healthcare' },
  { value: 'FINANCIAL_SERVICES', label: 'Financial Services' },
  { value: 'EDUCATION', label: 'Education' },
  { value: 'MANUFACTURING', label: 'Manufacturing' },
  { value: 'RETAIL', label: 'Retail' },
  { value: 'NON_PROFIT', label: 'Non-Profit' },
  { value: 'OTHER', label: 'Other' }
];

const SIZE_CATEGORIES = [
  { value: 'MICRO', label: 'Micro (1-9 employees)' },
  { value: 'SMALL', label: 'Small (10-49 employees)' },
  { value: 'MEDIUM', label: 'Medium (50-249 employees)' },
  { value: 'LARGE', label: 'Large (250-999 employees)' },
  { value: 'ENTERPRISE', label: 'Enterprise (1000+ employees)' }
];

const REVENUE_RANGES = [
  { value: 'PRE_REVENUE', label: 'Pre-Revenue' },
  { value: 'UNDER_1M', label: 'Under $1M' },
  { value: '1M_5M', label: '$1M - $5M' },
  { value: '5M_20M', label: '$5M - $20M' },
  { value: '20M_50M', label: '$20M - $50M' },
  { value: '50M_100M', label: '$50M - $100M' },
  { value: 'OVER_100M', label: 'Over $100M' }
];

const GROWTH_PHASES = [
  { value: 'PRE_STAGE', label: 'Pre-Stage' },
  { value: 'EARLY_STAGE', label: 'Early Stage' },
  { value: 'GROWTH_STAGE', label: 'Growth Stage' },
  { value: 'SCALING', label: 'Scaling' },
  { value: 'MATURE', label: 'Mature' },
  { value: 'TRANSFORMATION', label: 'Transformation' }
];

const REGIONS = [
  { value: 'ATX', label: 'Austin (ATX)' },
  { value: 'NWA', label: 'Northwest Arkansas (NWA)' },
  { value: 'DENVER', label: 'Denver' },
  { value: 'VIRGINIA', label: 'Virginia' },
  { value: 'OTHER', label: 'Other' }
];

const ENGAGEMENT_TYPES = [
  { value: 'GROWTH_WORKSHOP', label: 'Growth Workshop' },
  { value: 'LEADERSHIP_ACCELERATOR', label: 'Leadership Accelerator' },
  { value: 'FULL_GROWTH_PROGRAM', label: 'Full Growth Program' },
  { value: 'CUSTOM', label: 'Custom Engagement' }
];

const FOCUS_DIMENSIONS = [
  { value: 'LEADERSHIP', label: 'Leadership' },
  { value: 'CULTURE', label: 'Culture' },
  { value: 'STRATEGY', label: 'Strategy' },
  { value: 'OPERATIONS', label: 'Operations' },
  { value: 'TEAM', label: 'Team' }
];

const CATEGORIZATION_REASONS = [
  { value: 'SUB_OPTIMAL_RESOURCES', label: 'Sub-optimal Resources' },
  { value: 'LIMITED_NETWORK', label: 'Limited Network' },
  { value: 'LIMITED_CAPITAL', label: 'Limited Capital' },
  { value: 'NONPROFIT', label: 'Non-profit Organization' },
  { value: 'NEW_TO_COMMUNITY', label: 'New to Community' },
  { value: 'MINORITY_FOUNDER', label: 'Minority Founder' },
  { value: 'OTHER', label: 'Other' }
];

const SOURCE_OPTIONS = [
  { value: 'DIRECT_OUTREACH', label: 'Direct Outreach' },
  { value: 'REFERRAL', label: 'Referral' },
  { value: 'PARTNER', label: 'Partner' },
  { value: 'WEBSITE', label: 'Website' },
  { value: 'EVENT', label: 'Event' },
  { value: 'OTHER', label: 'Other' }
];

const PRIVACY_LEVELS = [
  { value: 'STANDARD', label: 'Standard', description: 'Basic privacy protection' },
  { value: 'ENHANCED', label: 'Enhanced', description: 'Additional privacy measures' },
  { value: 'MAXIMUM', label: 'Maximum', description: 'Highest level of privacy' }
];

// eslint-disable-next-line max-lines-per-function
function ClientCreationForm() {
  const { user } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [openDialog, setOpenDialog] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  // Form data state
  const [formData, setFormData] = useState({
    // Company Information
    legal_name: '',
    trading_name: '',
    industry: '',
    size_category: '',
    employee_count: '',
    annual_revenue: '',
    founded_date: '',
    growth_phase: '',
    headquarters_location: '',
    region: '',
    website: '',
    mission: '',
    vision: '',
    values: [],

    // Primary Contact
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    primary_role: '',
    linkedin_url: '',

    // Engagement Details
    start_date: '',
    end_date: '',
    engagement_type: '',
    focus_dimensions: [],
    lead_facilitator_id: '',

    // Initial Assessment
    assessment_growth_phase: '',
    culture_score: 0,
    leadership_score: 0,
    strategy_score: 0,
    observations: '',
    key_strengths: [],
    key_challenges: [],

    // Underestimated Founder Assessment
    is_underestimated: false,
    categorization_reasons: [],
    has_vision: false,
    has_market_insight: false,
    quality_courage: 0,
    quality_vulnerability: 0,
    quality_purpose: 0,
    quality_learning: 0,
    quality_determination: 0,
    quality_awareness: 0,

    // Privacy & Permissions
    privacy_level: 'STANDARD',
    data_collective_opt_in: false,
    partner_network_opt_in: false,

    // Source Information
    source: '',
    referrer_id: '',
    partner_id: '',
    event_name: '',
    other_source: ''
  });

  // Define form steps
  const steps = [
    {
      label: 'Company Information',
      icon: <Business />,
      description: 'Basic company details and background',
      fields: ['legal_name', 'trading_name', 'industry', 'size_category', 'employee_count', 'annual_revenue', 'founded_date', 'growth_phase', 'headquarters_location', 'region', 'website']
    },
    {
      label: 'Culture & Values',
      icon: <Psychology />,
      description: 'Mission, vision, and core values',
      fields: ['mission', 'vision', 'values'],
      optional: true
    },
    {
      label: 'Primary Contact',
      icon: <Person />,
      description: 'Main point of contact',
      fields: ['first_name', 'last_name', 'email', 'phone', 'primary_role', 'linkedin_url']
    },
    {
      label: 'Engagement',
      icon: <Assignment />,
      description: 'Program details and focus areas',
      fields: ['start_date', 'end_date', 'engagement_type', 'focus_dimensions', 'lead_facilitator_id']
    },
    {
      label: 'Assessment',
      icon: <Psychology />,
      description: 'Initial assessment and observations',
      fields: ['assessment_growth_phase', 'culture_score', 'leadership_score', 'strategy_score', 'observations', 'key_strengths', 'key_challenges'],
      optional: true
    },
    {
      label: 'Privacy & Source',
      icon: <Security />,
      description: 'Privacy settings and referral source',
      fields: ['privacy_level', 'data_collective_opt_in', 'partner_network_opt_in', 'source', 'referrer_id', 'partner_id', 'event_name', 'other_source']
    }
  ];

  // Validation rules
  const validateField = (name, value) => {
    switch (name) {
      case 'legal_name':
        return value.length >= 3 ? '' : 'Company name must be at least 3 characters';
      case 'email':
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? '' : 'Invalid email address';
      case 'phone':
        return value.length >= 10 || value === '' ? '' : 'Invalid phone number';
      case 'website':
        return value === '' || /^https?:\/\/.+/.test(value) ? '' : 'Website must start with http:// or https://';
      case 'employee_count':
        return value === '' || (parseInt(value) > 0) ? '' : 'Must be a positive number';
      default:
        return '';
    }
  };

  // Handle field changes
  const handleFieldChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    setTouchedFields(prev => ({ ...prev, [name]: true }));

    // Validate field
    const error = validateField(name, value);
    setValidationErrors(prev => ({ ...prev, [name]: error }));
  };

  // Handle blur event
  const handleFieldBlur = (name) => {
    setTouchedFields(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, formData[name]);
    setValidationErrors(prev => ({ ...prev, [name]: error }));
  };

  // Check if current step is valid
  const isStepValid = (stepIndex) => {
    const step = steps[stepIndex];
    if (step.optional) return true;

    const requiredFields = step.fields.filter(field => {
      // Define which fields are required
      const required = ['legal_name', 'industry', 'first_name', 'last_name', 'email', 'engagement_type'];
      return required.includes(field);
    });

    return requiredFields.every(field => {
      const value = formData[field];
      const error = validateField(field, value);
      return value && !error;
    });
  };

  // Calculate form completion percentage
  const calculateProgress = () => {
    const totalFields = Object.keys(formData).length;
    const filledFields = Object.values(formData).filter(value =>
      value !== '' && value !== 0 && value !== false && (!Array.isArray(value) || value.length > 0)
    ).length;
    return Math.round((filledFields / totalFields) * 100);
  };

  // Handle step navigation
  const handleNext = () => {
    if (isStepValid(activeStep)) {
      setActiveStep(prev => prev + 1);
    } else {
      // Mark all fields in current step as touched to show validation errors
      const currentStepFields = steps[activeStep].fields;
      const newTouchedFields = {};
      currentStepFields.forEach(field => {
        newTouchedFields[field] = true;
      });
      setTouchedFields(prev => ({ ...prev, ...newTouchedFields }));
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  // Handle form submission
  const handleSubmit = async () => {
    setOpenDialog(false);
    setIsLoading(true);

    try {
      // Prepare client data - using correct field names for the provision endpoint
      const clientData = {
        entityName: formData.legal_name,  // All entities require an entityName
        provisioningOptions: {  // Use camelCase as expected by API
          createGcsBucket: true,
          createBigQueryDataset: true,
          enableAnalytics: true,
          createGoogleDrive: true,
          createIngestionPoint: true
        },
        additionalData: {  // Use camelCase as expected by API
          ...formData,
          source: 'comprehensive_form',
          created_by: user?.email || '',
          created_by_uid: user?.uid || ''
        }
      };

      // Use the API service to create the client
      const data = await api.createClient(clientData);

      setSnackbarMessage(`Client "${data.name}" has been successfully created!`);
      setSnackbarSeverity('success');
      setOpenSnackbar(true);

      // Reset form after short delay
      setTimeout(() => {
        setFormData({
          legal_name: '',
          trading_name: '',
          industry: '',
          size_category: '',
          employee_count: '',
          annual_revenue: '',
          founded_date: '',
          growth_phase: '',
          headquarters_location: '',
          region: '',
          website: '',
          mission: '',
          vision: '',
          values: [],
          first_name: '',
          last_name: '',
          email: '',
          phone: '',
          primary_role: '',
          linkedin_url: '',
          start_date: '',
          end_date: '',
          engagement_type: '',
          focus_dimensions: [],
          lead_facilitator_id: '',
          assessment_growth_phase: '',
          culture_score: 0,
          leadership_score: 0,
          strategy_score: 0,
          observations: '',
          key_strengths: [],
          key_challenges: [],
          is_underestimated: false,
          categorization_reasons: [],
          has_vision: false,
          has_market_insight: false,
          quality_courage: 0,
          quality_vulnerability: 0,
          quality_purpose: 0,
          quality_learning: 0,
          quality_determination: 0,
          quality_awareness: 0,
          privacy_level: 'STANDARD',
          data_collective_opt_in: false,
          partner_network_opt_in: false,
          source: '',
          referrer_id: '',
          partner_id: '',
          event_name: '',
          other_source: ''
        });
        setActiveStep(0);
        setTouchedFields({});
        setValidationErrors({});
      }, 2000);

    } catch (error) {
      console.error('Error during client creation:', error);
      setSnackbarMessage(error.message || 'An error occurred while creating the client');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Render form fields based on type
  // eslint-disable-next-line max-lines-per-function
  const renderField = (fieldName) => {
    const value = formData[fieldName];
    const error = touchedFields[fieldName] && validationErrors[fieldName];

    switch (fieldName) {
      case 'legal_name':
      case 'trading_name':
      case 'headquarters_location':
      case 'website':
      case 'first_name':
      case 'last_name':
      case 'email':
      case 'phone':
      case 'primary_role':
      case 'linkedin_url':
      case 'lead_facilitator_id':
      case 'observations':
      case 'referrer_id':
      case 'partner_id':
      case 'event_name':
      case 'other_source':
        return (
          <TextField
            fullWidth
            label={fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            value={value}
            onChange={(e) => handleFieldChange(fieldName, e.target.value)}
            onBlur={() => handleFieldBlur(fieldName)}
            error={!!error}
            helperText={error}
            multiline={fieldName === 'observations'}
            rows={fieldName === 'observations' ? 4 : 1}
            required={['legal_name', 'first_name', 'last_name', 'email'].includes(fieldName)}
          />
        );

      case 'mission':
      case 'vision':
        return (
          <TextField
            fullWidth
            label={fieldName.charAt(0).toUpperCase() + fieldName.slice(1)}
            value={value}
            onChange={(e) => handleFieldChange(fieldName, e.target.value)}
            multiline
            rows={3}
          />
        );

      case 'employee_count':
        return (
          <TextField
            fullWidth
            label="Employee Count"
            type="number"
            value={value}
            onChange={(e) => handleFieldChange(fieldName, e.target.value)}
            onBlur={() => handleFieldBlur(fieldName)}
            error={!!error}
            helperText={error}
            InputProps={{ inputProps: { min: 1 } }}
          />
        );

      case 'industry':
        return (
          <FormControl fullWidth required error={!!error}>
            <InputLabel>Industry</InputLabel>
            <Select
              value={value}
              onChange={(e) => handleFieldChange(fieldName, e.target.value)}
              onBlur={() => handleFieldBlur(fieldName)}
              label="Industry"
            >
              {INDUSTRY_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {error && <FormHelperText>{error}</FormHelperText>}
          </FormControl>
        );

      case 'size_category':
        return (
          <FormControl fullWidth>
            <InputLabel>Company Size</InputLabel>
            <Select
              value={value}
              onChange={(e) => handleFieldChange(fieldName, e.target.value)}
              label="Company Size"
            >
              {SIZE_CATEGORIES.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'annual_revenue':
        return (
          <FormControl fullWidth>
            <InputLabel>Annual Revenue</InputLabel>
            <Select
              value={value}
              onChange={(e) => handleFieldChange(fieldName, e.target.value)}
              label="Annual Revenue"
            >
              {REVENUE_RANGES.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'growth_phase':
      case 'assessment_growth_phase':
        return (
          <FormControl fullWidth>
            <InputLabel>{fieldName === 'growth_phase' ? 'Growth Phase' : 'Current Growth Phase'}</InputLabel>
            <Select
              value={value}
              onChange={(e) => handleFieldChange(fieldName, e.target.value)}
              label={fieldName === 'growth_phase' ? 'Growth Phase' : 'Current Growth Phase'}
            >
              {GROWTH_PHASES.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'region':
        return (
          <FormControl fullWidth>
            <InputLabel>Region</InputLabel>
            <Select
              value={value}
              onChange={(e) => handleFieldChange(fieldName, e.target.value)}
              label="Region"
            >
              {REGIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'engagement_type':
        return (
          <FormControl fullWidth required>
            <InputLabel>Engagement Type</InputLabel>
            <Select
              value={value}
              onChange={(e) => handleFieldChange(fieldName, e.target.value)}
              label="Engagement Type"
            >
              {ENGAGEMENT_TYPES.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'source':
        return (
          <FormControl fullWidth>
            <InputLabel>Source</InputLabel>
            <Select
              value={value}
              onChange={(e) => handleFieldChange(fieldName, e.target.value)}
              label="Source"
            >
              {SOURCE_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'founded_date':
      case 'start_date':
      case 'end_date':
        return (
          <TextField
            fullWidth
            label={fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            type="date"
            value={value}
            onChange={(e) => handleFieldChange(fieldName, e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        );

      case 'values':
      case 'key_strengths':
      case 'key_challenges':
        return (
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              {value.map((item, index) => (
                <Chip
                  key={index}
                  label={item}
                  onDelete={() => {
                    const newValue = value.filter((_, i) => i !== index);
                    handleFieldChange(fieldName, newValue);
                  }}
                />
              ))}
            </Box>
            <TextField
              fullWidth
              placeholder={`Add ${fieldName.replace(/_/g, ' ')}`}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && e.target.value.trim()) {
                  e.preventDefault();
                  handleFieldChange(fieldName, [...value, e.target.value.trim()]);
                  e.target.value = '';
                }
              }}
              helperText="Press Enter to add"
              size="small"
            />
          </Box>
        );

      case 'focus_dimensions':
        return (
          <FormControl fullWidth>
            <InputLabel>Focus Dimensions</InputLabel>
            <Select
              multiple
              value={value}
              onChange={(e) => handleFieldChange(fieldName, e.target.value)}
              input={<OutlinedInput label="Focus Dimensions" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((val) => (
                    <Chip key={val} label={FOCUS_DIMENSIONS.find(d => d.value === val)?.label || val} size="small" />
                  ))}
                </Box>
              )}
            >
              {FOCUS_DIMENSIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox checked={value.indexOf(option.value) > -1} />
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'categorization_reasons':
        return (
          <FormControl fullWidth disabled={!formData.is_underestimated}>
            <InputLabel>Categorization Reasons</InputLabel>
            <Select
              multiple
              value={value}
              onChange={(e) => handleFieldChange(fieldName, e.target.value)}
              input={<OutlinedInput label="Categorization Reasons" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((val) => (
                    <Chip key={val} label={CATEGORIZATION_REASONS.find(r => r.value === val)?.label || val} size="small" />
                  ))}
                </Box>
              )}
            >
              {CATEGORIZATION_REASONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox checked={value.indexOf(option.value) > -1} />
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'culture_score':
      case 'leadership_score':
      case 'strategy_score':
        return (
          <Box>
            <Typography gutterBottom>
              {fieldName.replace(/_score/g, '').replace(/\b\w/g, l => l.toUpperCase())} Score: {value || 0}
            </Typography>
            <Box sx={{ px: 2 }}>
              <input
                type="range"
                min="0"
                max="10"
                value={value || 0}
                onChange={(e) => handleFieldChange(fieldName, parseInt(e.target.value))}
                style={{ width: '100%' }}
              />
            </Box>
          </Box>
        );

      case 'is_underestimated':
      case 'has_vision':
      case 'has_market_insight':
      case 'data_collective_opt_in':
      case 'partner_network_opt_in':
        return (
          <FormControlLabel
            control={
              <Checkbox
                checked={value}
                onChange={(e) => handleFieldChange(fieldName, e.target.checked)}
              />
            }
            label={fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
          />
        );

      case 'quality_courage':
      case 'quality_vulnerability':
      case 'quality_purpose':
      case 'quality_learning':
      case 'quality_determination':
      case 'quality_awareness':
        return (
          <Box>
            <FormLabel component="legend">
              {fieldName.replace(/quality_/g, '').replace(/\b\w/g, l => l.toUpperCase())}
            </FormLabel>
            <RadioGroup
              row
              value={value}
              onChange={(e) => handleFieldChange(fieldName, parseInt(e.target.value))}
            >
              <FormControlLabel value={1} control={<Radio />} label="Low" disabled={!formData.is_underestimated} />
              <FormControlLabel value={2} control={<Radio />} label="Medium" disabled={!formData.is_underestimated} />
              <FormControlLabel value={3} control={<Radio />} label="High" disabled={!formData.is_underestimated} />
            </RadioGroup>
          </Box>
        );

      case 'privacy_level':
        return (
          <FormControl fullWidth>
            <FormLabel component="legend">Privacy Level</FormLabel>
            <RadioGroup
              value={value}
              onChange={(e) => handleFieldChange(fieldName, e.target.value)}
            >
              {PRIVACY_LEVELS.map(level => (
                <FormControlLabel
                  key={level.value}
                  value={level.value}
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1">{level.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {level.description}
                      </Typography>
                    </Box>
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>
        );

      default:
        return null;
    }
  };

  // Render step content
  const renderStepContent = (step) => {
    return (
      <Grid container spacing={3}>
        {step.fields.map(field => (
          <Grid
            key={field}
            size={{
              xs: 12,
              md: field === 'observations' ? 12 : 6
            }}>
            {renderField(field)}
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 2, maxWidth: 1200, mx: 'auto' }}>
      {/* Header with progress */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Business color="primary" />
          Create New Client
        </Typography>
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Overall Progress
            </Typography>
            <Typography variant="body2" color="primary" fontWeight="bold">
              {calculateProgress()}%
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={calculateProgress()} sx={{ height: 8, borderRadius: 4 }} />
        </Box>
      </Box>
      {/* Stepper */}
      <Stepper activeStep={activeStep} orientation="vertical">
      {/* eslint-disable-next-line max-lines-per-function */}
        {steps.map((step, index) => (
          <Step key={step.label}>
            <StepLabel
              optional={step.optional && (
                <Typography variant="caption">Optional</Typography>
              )}
              StepIconComponent={() => (
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: activeStep >= index ? 'primary.main' : 'grey.300',
                    color: 'white'
                  }}
                >
                  {activeStep > index ? <CheckCircle /> : step.icon}
                </Box>
              )}
            >
              <Typography variant="h6">{step.label}</Typography>
              <Typography variant="body2" color="text.secondary">
                {step.description}
              </Typography>
            </StepLabel>
            <StepContent>
              <Fade in={activeStep === index}>
                <Box sx={{ mt: 2, mb: 3 }}>
                  {renderStepContent(step)}

                  {/* Special handling for underestimated founder section */}
                  {step.label === 'Assessment' && (
                    <Box sx={{ mt: 3 }}>
                      <Divider sx={{ mb: 2 }}>
                        <Chip label="Underestimated Founder Assessment" size="small" />
                      </Divider>
                      <Grid container spacing={2}>
                        <Grid size={12}>
                          {renderField('is_underestimated')}
                        </Grid>
                        {formData.is_underestimated && (
                          <>
                            <Grid
                              size={{
                                xs: 12,
                                md: 6
                              }}>
                              {renderField('categorization_reasons')}
                            </Grid>
                            <Grid
                              size={{
                                xs: 12,
                                md: 6
                              }}>
                              <Stack spacing={1}>
                                {renderField('has_vision')}
                                {renderField('has_market_insight')}
                              </Stack>
                            </Grid>
                            <Grid size={12}>
                              <Typography variant="subtitle2" gutterBottom>
                                Leadership Qualities
                              </Typography>
                              <Grid container spacing={2}>
                                <Grid
                                  size={{
                                    xs: 12,
                                    md: 6
                                  }}>
                                  {renderField('quality_courage')}
                                </Grid>
                                <Grid
                                  size={{
                                    xs: 12,
                                    md: 6
                                  }}>
                                  {renderField('quality_vulnerability')}
                                </Grid>
                                <Grid
                                  size={{
                                    xs: 12,
                                    md: 6
                                  }}>
                                  {renderField('quality_purpose')}
                                </Grid>
                                <Grid
                                  size={{
                                    xs: 12,
                                    md: 6
                                  }}>
                                  {renderField('quality_learning')}
                                </Grid>
                                <Grid
                                  size={{
                                    xs: 12,
                                    md: 6
                                  }}>
                                  {renderField('quality_determination')}
                                </Grid>
                                <Grid
                                  size={{
                                    xs: 12,
                                    md: 6
                                  }}>
                                  {renderField('quality_awareness')}
                                </Grid>
                              </Grid>
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Box>
                  )}
                </Box>
              </Fade>

              {/* Step navigation */}
              <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  startIcon={<ArrowForward />}
                  disabled={!isStepValid(index) && !step.optional}
                >
                  {index === steps.length - 1 ? 'Review & Submit' : 'Next'}
                </Button>
                {index > 0 && (
                  <Button
                    onClick={handleBack}
                    startIcon={<ArrowBack />}
                  >
                    Back
                  </Button>
                )}
                {step.optional && (
                  <Button
                    onClick={handleNext}
                    variant="outlined"
                  >
                    Skip
                  </Button>
                )}
              </Box>
            </StepContent>
          </Step>
        ))}
      </Stepper>
      {/* Review and Submit */}
      {activeStep === steps.length && (
        <Fade in>
          <Box sx={{ mt: 3 }}>
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Ready to Create Client!
              </Typography>
              <Typography variant="body2">
                Please review your information and click "Create Client" to proceed.
              </Typography>
            </Alert>

            {/* Summary of key information */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Company
                    </Typography>
                    <Typography variant="h6">
                      {formData.legal_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formData.industry && INDUSTRY_OPTIONS.find(i => i.value === formData.industry)?.label}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Primary Contact
                    </Typography>
                    <Typography variant="h6">
                      {formData.first_name} {formData.last_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formData.email}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                onClick={handleBack}
                startIcon={<ArrowBack />}
              >
                Back
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setOpenDialog(true)}
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={20} /> : null}
              >
                {isLoading ? 'Creating...' : 'Create Client'}
              </Button>
            </Box>
          </Box>
        </Fade>
      )}
      {/* Confirmation Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Confirm Client Creation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to create a new client for "{formData.legal_name}"?
            This will provision the necessary resources and set up the client in the system.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit} color="primary" variant="contained" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      {/* Notification Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Paper>
  );
}

export default ClientCreationForm;
